import React from "react";
import { Button } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import appInsights from "../config/applicationInsights";
import StackTrace from "stacktrace-js";
import DownloadIcon from "@mui/icons-material/Download";
import { logs } from "../api/api"; // Ensure correct path
import { saveAs } from "file-saver";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: this.props.hasError ?? false, errorDetails: null };
    this.customError = this.props.customError ?? "Something went wrong.";
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    let errorDetails = {
      message: error.message,
      type: error.constructor.name,
      componentStack: info.componentStack,
      timestamp: new Date().toISOString(),
    };

    // Log the stack trace using stacktrace-js
    StackTrace.fromError(error)
      .then((stackFrames) => {
        const stackFramesArray = stackFrames.map((frame) => frame.toString());
        errorDetails.stackFrames = stackFramesArray;
        this.logErrorAndAPIRequests(errorDetails); // Log error and API requests together
      })
      .catch((error) => {
        console.error("Failed to fetch source map information:", error);
      });

    // Update state with error details
    this.setState({ errorDetails });

    // Additionally, track the exception with Application Insights or other services
    appInsights.trackException({
      exception: error,
      properties: { errorInfo: JSON.stringify(errorDetails) },
    });
  }

  logErrorAndAPIRequests = (errorDetails) => {
    // Combine error details and API logs
    const combinedLogs = [
      "=== Error Details ===",
      JSON.stringify(errorDetails, null, 2),
      "\n=== API Request Logs ===",
      logs.join("\n"),
    ].join("\n\n");

    // Save the combined logs as a text file
    const blob = new Blob([combinedLogs], { type: "text/plain;charset=utf-8" });
    saveAs(
      blob,
      `error_and_logs_${new Date().toISOString().split("T")[0]}.txt`
    );
  };

  handleRefresh = () => {
    // Refresh the page to try to recover from the error
    if (window.location.pathname.includes("agent")) {
      window.location = "/agent";
    } else {
      window.location = "/";
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
          }}
        >
          <ErrorOutlineIcon style={{ fontSize: 80, color: "#f44336" }} />
          <h1>{this.customError ?? "Something went wrong."}</h1>
          <p>
            {this.customError === "Something went wrong."
              ? "The error has been logged and will be fixed soon. You may also download the error details and send it to the developers."
              : "You may have visited an invalid URL."}
          </p>
          <Button
            variant="contained"
            color="primary"
            onClick={this.handleRefresh}
            style={{ marginTop: 20 }}
          >
            Reload App
          </Button>
          {this.customError === "Something went wrong." && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                // Manually trigger download if needed
                this.logErrorAndAPIRequests(this.state.errorDetails);
              }}
              style={{ marginTop: 20 }}
            >
              <DownloadIcon />
              Download Error and Logs
            </Button>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
